<script >
import ChartingLiteChart from '@/features/charting-next/components/ChartingLiteChart.vue';
import { fetchChartingFirstOhlc } from '@/features/charting-next/api/chartingInsightsRepository';
import { useRemoteResource } from '@/features/charting-next/services/useRemoteResource';
import { useChartingSession } from '@/features/charting-next/services/chartingSession';
import { computed, onBeforeUnmount, onMounted, ref, watch } from '@vue/composition-api';
const __sfc_main = {};
__sfc_main.props = {
  visible: {
    type: Boolean,
    default: false
  },
  stock: {
    type: Object,
    default: null
  },
  series: {
    type: Object,
    default: null
  },
  resolution: {
    type: String,
    default: 'M'
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const emit = __ctx.emit;
  const session = useChartingSession();
  // Check coverage only in the opened monthly chart, never once per feed card.
  const historyStart = useRemoteResource({
    key: () => ['charting', session.uid, 'history-start', props.stock?.ticker],
    loader: () => fetchChartingFirstOhlc(props.stock.ticker),
    enabled: () => session.isAuthenticated && props.visible && props.resolution === 'M' && Boolean(props.stock?.ticker),
    ttl: 300000
  });
  const hasFullHistory = computed(() => Boolean(props.series?.hasFullHistory) || historyStart.isReady.value && historyStart.data.value > 0 && props.series?.historyFrom === 0);
  const emptyItems = Object.freeze([]);
  const portraitOverlayVisibility = Object.freeze({
    support: true,
    stop: false,
    resistance: true,
    trendLine: true,
    trendPoint: true,
    riskRewardPoint: true
  });
  const landscapeOverlayVisibility = Object.freeze({
    ...portraitOverlayVisibility,
    stop: true
  });
  const resolutionLabels = Object.freeze({
    D: 'Diario',
    W: 'Semanal',
    M: 'Mensual'
  });
  const landscapeBodyClass = 'charting-mobile-chart-landscape-active';
  const isLandscape = ref(false);
  let orientationMedia = null;
  const syncOrientation = event => {
    isLandscape.value = Boolean(event?.matches ?? orientationMedia?.matches);
  };
  const syncLandscapeChrome = () => {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle(landscapeBodyClass, Boolean(props.visible && isLandscape.value));
  };
  watch([() => props.visible, isLandscape], syncLandscapeChrome);
  onMounted(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    orientationMedia = window.matchMedia('(orientation: landscape)');
    if (orientationMedia.addEventListener) orientationMedia.addEventListener('change', syncOrientation);else orientationMedia.addListener?.(syncOrientation);
    syncOrientation(orientationMedia);
  });
  onBeforeUnmount(() => {
    if (orientationMedia?.removeEventListener) orientationMedia.removeEventListener('change', syncOrientation);else orientationMedia?.removeListener?.(syncOrientation);
    if (typeof document !== 'undefined') document.body.classList.remove(landscapeBodyClass);
  });
  const companyName = computed(() => props.stock?.companyName || props.stock?.description || 'Sin nombre disponible');
  const candles = computed(() => props.series?.candles || emptyItems);
  const volumes = computed(() => props.series?.volumes || emptyItems);
  const corporateActions = computed(() => props.series?.corporateActions || emptyItems);
  const resolutionLabel = computed(() => resolutionLabels[props.resolution] || props.resolution);
  const overlayVisibility = computed(() => isLandscape.value ? landscapeOverlayVisibility : portraitOverlayVisibility);
  return {
    emit,
    hasFullHistory,
    isLandscape,
    companyName,
    candles,
    volumes,
    corporateActions,
    resolutionLabel,
    overlayVisibility
  };
};
__sfc_main.components = Object.assign({
  ChartingLiteChart
}, __sfc_main.components);
export default __sfc_main;
</script>

<template>
    <Dialog
        :visible="visible"
        modal
        :closable="!isLandscape"
        :close-on-escape="!isLandscape"
        mask-class="charting-mobile-chart-mask"
        dialog-class="charting-mobile-chart-dialog"
        :dialog-style="{ width: '100vw' }"
        @update:visible="emit('update:visible', $event)"
    >
        <template #header>
            <div class="charting-mobile-chart-view__heading">
                <div class="charting-mobile-chart-view__eyebrow"><i class="pi pi-chart-line" aria-hidden="true"></i> Gráfico completo</div>
                <div class="charting-mobile-chart-view__ticker-row">
                    <strong>{{ (stock && stock.ticker) || 'Gráfico' }}</strong>
                    <span v-if="stock && stock.entryTypeLabel">{{ stock.entryTypeLabel }}</span>
                    <b>{{ resolutionLabel }}</b>
                </div>
                <div class="charting-mobile-chart-view__company">{{ companyName }}</div>
            </div>
        </template>

        <section class="charting-mobile-chart-view">
            <div class="charting-mobile-chart-view__hint" :class="{ 'is-rotate-hint': !isLandscape, 'is-landscape-exit-hint': isLandscape }" aria-live="polite">
                <i class="pi pi-mobile" aria-hidden="true"></i>
                <span>{{ isLandscape ? 'Gira el móvil para volver a la vista vertical' : 'Gira el móvil para ver el gráfico completo' }}</span>
            </div>

            <div class="charting-mobile-chart-view__stage">
                <ChartingLiteChart
                    v-if="candles.length"
                    :key="isLandscape ? 'landscape' : 'portrait'"
                    :candles="candles"
                    :has-full-history="hasFullHistory"
                    :volumes="volumes"
                    :corporate-actions="corporateActions"
                    :analysis-overlays="(stock && stock.chartOverlays) || {}"
                    :overlay-visibility="overlayVisibility"
                    :resolution="resolution"
                    price-series-type="bars"
                    :show-volume="false"
                    :show-legend="false"
                    :show-left-price-scale="false"
                    :show-right-price-scale="isLandscape"
                    :show-current-price-line="isLandscape"
                    :interactive="true"
                    :ticker="(stock && stock.ticker) || ''"
                    :company-name="companyName"
                    :entry-type-label="(stock && stock.entryTypeLabel) || ''"
                    :series-key="`mobile-full:${(stock && stock.ticker) || ''}:${resolution}:${(stock && stock.activeEntryVariantKey) || 'default'}`"
                />
                <div v-else class="charting-mobile-chart-view__empty">
                    <i class="pi pi-chart-line" aria-hidden="true"></i>
                    <span>No hay histórico disponible para este valor.</span>
                </div>
            </div>
        </section>
    </Dialog>
</template>

<style scoped>
.charting-mobile-chart-view {
    display: flex;
    min-width: 0;
    min-height: 0;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 0.55rem;
}

.charting-mobile-chart-view__heading { min-width: 0; }
.charting-mobile-chart-view__eyebrow { display: flex; align-items: center; gap: 0.35rem; color: var(--modern-accent, var(--brand-primary)); font-size: 0.63rem; font-weight: 850; letter-spacing: 0.09em; text-transform: uppercase; }
.charting-mobile-chart-view__ticker-row { display: flex; min-width: 0; align-items: center; gap: 0.45rem; margin-top: 0.32rem; }
.charting-mobile-chart-view__ticker-row strong { font-size: 1.05rem; }
.charting-mobile-chart-view__ticker-row span,
.charting-mobile-chart-view__ticker-row b { border: 1px solid var(--modern-border, var(--charting-b-border-color)); border-radius: 999px; padding: 0.16rem 0.45rem; color: var(--modern-muted, var(--p-text-muted-color)); font-size: 0.62rem; font-weight: 800; }
.charting-mobile-chart-view__ticker-row b { border-color: color-mix(in srgb, var(--modern-accent, var(--brand-primary)), transparent 45%); color: var(--modern-accent, var(--brand-primary)); }
.charting-mobile-chart-view__company { max-width: 17rem; overflow: hidden; margin-top: 0.2rem; color: var(--modern-muted, var(--p-text-muted-color)); font-size: 0.72rem; text-overflow: ellipsis; white-space: nowrap; }

.charting-mobile-chart-view__hint { display: flex; flex: 0 0 auto; align-items: center; justify-content: center; gap: 0.4rem; color: var(--modern-muted, var(--p-text-muted-color)); font-size: 0.67rem; font-weight: 650; }
.charting-mobile-chart-view__hint.is-rotate-hint { color: var(--modern-accent, var(--brand-primary)); }
.charting-mobile-chart-view__hint.is-rotate-hint .pi { transform: rotate(-90deg); }
.charting-mobile-chart-view__stage { position: relative; min-width: 0; min-height: 0; flex: 1 1 auto; overflow: hidden; border: 1px solid var(--modern-border, var(--charting-b-border-color)); border-radius: 1rem; background: var(--charting-b-stage-bg); box-shadow: 0 10px 30px color-mix(in srgb, #0f172a 7%, transparent); }
.charting-mobile-chart-view__stage ::v-deep .charting-lite-chart { min-height: 0; }
.charting-mobile-chart-view__empty { display: flex; height: 100%; min-height: 18rem; align-items: center; justify-content: center; gap: 0.55rem; flex-direction: column; padding: 1rem; color: var(--modern-muted, var(--p-text-muted-color)); text-align: center; }
.charting-mobile-chart-view__empty .pi { color: var(--modern-accent, var(--brand-primary)); font-size: 1.5rem; }
</style>

<style>
@media (max-width: 699px), (orientation: landscape) and (max-height: 699px) {
    .p-dialog-mask.charting-mobile-chart-mask { top: var(--modern-shell-header-height, 62px); align-items: stretch; padding: 0; }
    .p-dialog.charting-mobile-chart-dialog { width: 100vw !important; height: calc(100dvh - var(--modern-shell-header-height, 62px)) !important; max-width: none; max-height: none; border: 0; border-radius: 0; background: var(--modern-bg, #f6f8fb); box-shadow: none; }
    .p-dialog.charting-mobile-chart-dialog .p-dialog-header { flex: 0 0 auto; border-color: var(--modern-border, var(--charting-b-border-color)); background: var(--modern-surface, #fff); padding: 0.75rem 1rem; }
    .p-dialog.charting-mobile-chart-dialog .p-dialog-header-close { background: var(--modern-bg, #f6f8fb); }
    .p-dialog.charting-mobile-chart-dialog .p-dialog-content { display: flex; min-width: 0; min-height: 0; flex: 1 1 auto; overflow: hidden; background: var(--modern-bg, #f6f8fb); padding: 0.6rem 0.65rem max(0.65rem, env(safe-area-inset-bottom)); }
}

@media (orientation: landscape) and (max-height: 699px) {
    body.charting-mobile-chart-landscape-active { overflow: hidden; }
    body.charting-mobile-chart-landscape-active .modern-topbar { display: none !important; }
    body.charting-mobile-chart-landscape-active .p-dialog-mask.charting-mobile-chart-mask { top: 0; }
    body.charting-mobile-chart-landscape-active .p-dialog.charting-mobile-chart-dialog { height: 100vh !important; height: 100dvh !important; }
    body.charting-mobile-chart-landscape-active .p-dialog.charting-mobile-chart-dialog .p-dialog-header { display: none; }
    body.charting-mobile-chart-landscape-active .p-dialog.charting-mobile-chart-dialog .p-dialog-content { padding: 0; }
    body.charting-mobile-chart-landscape-active .charting-mobile-chart-view { position: relative; gap: 0; }
    body.charting-mobile-chart-landscape-active .charting-mobile-chart-view__stage { border: 0; border-radius: 0; box-shadow: none; }
    body.charting-mobile-chart-landscape-active .charting-mobile-chart-view__hint.is-landscape-exit-hint {
        position: absolute;
        z-index: 8;
        top: max(0.35rem, env(safe-area-inset-top));
        left: 50%;
        border: 1px solid color-mix(in srgb, var(--modern-border, var(--charting-b-border-color)), transparent 18%);
        border-radius: 999px;
        background: color-mix(in srgb, var(--modern-surface, #fff) 88%, transparent);
        padding: 0.25rem 0.55rem;
        box-shadow: 0 5px 18px color-mix(in srgb, #0f172a 14%, transparent);
        color: var(--modern-muted, var(--p-text-muted-color));
        pointer-events: none;
        transform: translateX(-50%);
        backdrop-filter: blur(8px);
    }
}
</style>
